import { Checkbox } from '@heidi/design-system/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@heidi/design-system/command';
import {
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@heidi/design-system/dropdown-menu';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { ChevronRight, Stethoscope } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useClinicianFilter } from './hooks/useClinicianFilter';

export const ClinicianFilter = () => {
  const intl = useIntl();
  const {
    isOpen,
    setIsOpen,
    clinicians,
    selectedClinicianIds,
    selectedClinicianIdsSet,
    searchValue,
    setSearchValue,
    handleFilter,
    handleSelect,
    isClinicianFilterApplicable,
    isLoading,
  } = useClinicianFilter();

  if (!isClinicianFilterApplicable) {
    return null;
  }

  const searchClinicians = intl.formatMessage({
    defaultMessage: 'Search clinicians',
    id: 'LC47tM7N35',
  });

  return (
    <DropdownMenuSub open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuSubTrigger
        hideChevron
        className="items-center justify-between gap-2 py-1.5 pe-1.5 ps-2.5"
      >
        <div className="flex items-center gap-2">
          <Stethoscope size={16} className="min-w-4" aria-hidden="true" />
          <div className="flex flex-col justify-center gap-0.5">
            <TypographyP2>
              {intl.formatMessage({
                defaultMessage: 'Clinician',
                id: 'ny2hz2/I2g',
              })}
            </TypographyP2>
            {selectedClinicianIds.length > 0 && (
              <TypographyCaption className="text-text-secondary">
                {intl.formatMessage(
                  {
                    defaultMessage: '{count} selected',
                    id: 'NKPfmmHQdx',
                  },
                  { count: selectedClinicianIds.length }
                )}
              </TypographyCaption>
            )}
          </div>
        </div>
        <ChevronRight
          size={16}
          className="min-w-4 text-text-secondary rtl:-scale-x-100"
          aria-hidden="true"
        />
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent sideOffset={4} className="w-screen max-w-64">
          <Command filter={handleFilter} label={searchClinicians}>
            <CommandInput
              value={searchValue}
              onValueChange={setSearchValue}
              placeholder={searchClinicians}
              aria-label={searchClinicians}
              className="h-9 py-0"
            />
            <CommandList>
              <CommandEmpty>
                {isLoading
                  ? intl.formatMessage({
                      defaultMessage: 'Loading...',
                      id: 'gjBiyjshwX',
                    })
                  : intl.formatMessage({
                      defaultMessage: 'No clinicians found',
                      id: 'R3ayvpQW5I',
                    })}
              </CommandEmpty>
              <CommandGroup>
                {clinicians.map((clinician) => (
                  <CommandItem
                    key={clinician.kindeUserId}
                    onSelect={() => handleSelect(clinician.kindeUserId)}
                    value={clinician.kindeUserId}
                    className="flex items-center gap-2"
                  >
                    <Checkbox
                      checked={selectedClinicianIdsSet.has(clinician.kindeUserId)}
                      aria-hidden="true"
                      tabIndex={-1}
                    />
                    <TypographyP2 className="truncate">{clinician.name}</TypographyP2>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};
